import * as React from 'react';
import { styled } from '@mui/material/styles';
import { ImageLoadingSkeleton } from '../../../shared/imageCard/view/ImageLoadingSkeleton';

const ImageContainer = styled('figure')({
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    overflow: 'hidden',
    padding: '16px'
});

const ImageWrapper = styled('div')({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '100%'
});

const EditedImage = styled('img')<{ loaded: boolean }>(({ loaded }) => ({
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
    opacity: loaded ? 1 : 0,
    transition: 'opacity 200ms ease-in'
}));

const SkeletonContainer = styled('div')({
    width: '80%',
    height: '70%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    overflow: 'hidden'
});

type EditedImageDisplayProps = {
    src: string;
    alt: string;
    overlay?: React.ReactNode;
    onLoad?: () => void;
    onError?: () => void;
};

export function EditedImageDisplay({
    src,
    alt,
    overlay,
    onLoad,
    onError
}: EditedImageDisplayProps) {
    let [imageLoaded, setImageLoaded] = React.useState(false);
    let [aspectRatio, setAspectRatio] = React.useState<string | undefined>(undefined);
    let showSkeleton = !src || !imageLoaded;

    function showImage(image: HTMLImageElement) {
        let { naturalWidth, naturalHeight } = image;
        if (naturalWidth > 0 && naturalHeight > 0) {
            setAspectRatio(`${naturalWidth} / ${naturalHeight}`);
        }
        setImageLoaded(true);
        if (onLoad) {
            onLoad();
        }
    }

    function showImageAlreadyInBrowserCache(image: HTMLImageElement | null) {
        if (image && !imageLoaded && image.complete && image.naturalWidth > 0) {
            showImage(image);
        }
    }

    return (
        <ImageContainer>
            {showSkeleton && (
                <SkeletonContainer>
                    <ImageLoadingSkeleton />
                </SkeletonContainer>
            )}
            {src && (
                <ImageWrapper
                    style={
                        showSkeleton
                            ? { position: 'absolute', visibility: 'hidden' }
                            : { aspectRatio }
                    }
                >
                    <EditedImage
                        ref={showImageAlreadyInBrowserCache}
                        src={src}
                        alt={alt}
                        loaded={imageLoaded}
                        onLoad={(event) => showImage(event.currentTarget)}
                        onError={onError}
                    />
                    {imageLoaded ? overlay : null}
                </ImageWrapper>
            )}
        </ImageContainer>
    );
}
